import { createContext, useContext, useMemo } from 'react'
import { useMatch } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { eventService } from '@services/eventService'
import { addDays, startOfToday } from 'date-fns'
import { CITIES } from '@utils/geolocation'
import { useCityContext } from './LocationContext'
import { parseDate } from '@utils/formatters'
import { checkIsEventEnded } from '@utils/eventDateUtils'

const EventsContext = createContext(null)

export const EventsProvider = ({ children }) => {
  const { city } = useCityContext()
  const unitId = city?.unit_id || CITIES.uberlandia.unit_id
  const isHomeRoute = Boolean(useMatch('/:citySlug'))

  const { data: fetchedEvents = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['events', city?.slug || 'default', unitId],
    queryFn: async () => {
      if (!unitId) return []
      const startOfTodayDate = startOfToday()
      const endDate = addDays(startOfTodayDate, 30)
      return await eventService.getAll(unitId, startOfTodayDate, endDate)
    },
    enabled: Boolean(city),
    staleTime: 15 * 1000, // 15 seconds stale time
    refetchInterval: isHomeRoute ? 30 * 1000 : false, // Auto-update prices/stock every 30 seconds only on home
    refetchIntervalInBackground: false, // Do not poll when tab is in background
    refetchOnWindowFocus: Boolean(isHomeRoute),
  })

  const { featuredEvent, upcomingEvents, nextEvents } = useMemo(() => {
    if (!fetchedEvents || fetchedEvents.length === 0) {
      return { featuredEvent: null, upcomingEvents: [], nextEvents: [] }
    }

    const now = new Date()
    const activeEvents = fetchedEvents
      .filter(event => !checkIsEventEnded(event))
      .sort((a, b) => {
        const dateA = a.start_date ? parseDate(a.start_date) : (a.date ? parseDate(a.date) : new Date())
        const dateB = b.start_date ? parseDate(b.start_date) : (b.date ? parseDate(b.date) : new Date())
        return dateA - dateB
      })

    const eventsCopy = [...activeEvents]
    let nextFeaturedEvent = null

    // 1. Prioritize closest event marked as main event with sales open
    let featuredIndex = eventsCopy.findIndex(event => {
      const isMain = Boolean(event.is_main_event)
      const salesStart = event.sales_start_at
      const isSalesOpen = !salesStart || parseDate(salesStart) <= now
      return isMain && isSalesOpen
    })

    // 2. Fallback: closest event with sales open
    if (featuredIndex === -1) {
      featuredIndex = eventsCopy.findIndex(event => {
        const salesStart = event.sales_start_at
        return !salesStart || parseDate(salesStart) <= now
      })
    }

    if (featuredIndex !== -1) {
      nextFeaturedEvent = eventsCopy[featuredIndex]
      eventsCopy.splice(featuredIndex, 1)
    }

    const finalUpcomingEvents = eventsCopy.filter(event => {
      const salesStart = event.sales_start_at
      return !salesStart || parseDate(salesStart) <= now
    })

    const finalNextEvents = eventsCopy.filter(event => {
      const salesStart = event.sales_start_at
      return salesStart && parseDate(salesStart) > now
    })

    return {
      featuredEvent: nextFeaturedEvent,
      upcomingEvents: finalUpcomingEvents,
      nextEvents: finalNextEvents,
    }
  }, [fetchedEvents])

  const value = useMemo(
    () => ({
      upcomingEvents,
      nextEvents,
      featuredEvent,
      eventsLoading,
      nextEventsLoading: eventsLoading,
      lastFetched: new Date(),
    }),
    [upcomingEvents, nextEvents, featuredEvent, eventsLoading]
  )

  return (
    <EventsContext.Provider value={value}>
      {children}
    </EventsContext.Provider>
  )
}

export const useEvents = () => {
  const context = useContext(EventsContext)

  if (!context) {
    throw new Error('useEvents must be used within an EventsProvider')
  }

  return context
}